import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import useLogError from '../../../hooks/useLogError';
import {
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GetItemsResponse, ItemRequest, WasteCheckSheetRequest, WasteCheckSheetResponse } from '../../../app/services/api/generated';
import { ExtendedGetItemsResponse } from '../interfaces/ExtendedGetItemsResponse';
import { ItemWasteChecksheetData, SaveStatus } from '../interfaces/ItemWasteChecksheetData';
import { ItemPreview } from '../interfaces/ItemsPreview';
import { Category } from '../interfaces/Category';

export const useWasteChecksheetDetail = () => {
  const { getItems, getWasteById } = useNswagClient();
  const [itemsData, setItemsData] = useState<ExtendedGetItemsResponse>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);
  const { id } = useParams<{ id: string | undefined }>();
  const parsedId = id ? parseInt(id, 10) : 0;

  const theme = useTheme();
  const { selectedStore } = useContext(UserContext);

  const [isArchived, setIsArchived] = useState<boolean>(false);
  const [submittedBy, setSubmittedBy] = useState<string>('');
  const [submittedDate, setSubmittedDate] = useState<string | undefined>('');

  const [isPrinting, setIsPrinting] = useState(false);
  const [showSavedAlert, setShowSavedAlert] = useState(false);
  const [showErrorSavedAlert, setShowErrorSavedAlert] = useState(false);
  const [showErrorSubmitAlert, setShowErrorSubmitAlert] = useState(false);
  const [openSubmitModal, setOpenSubmitModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemsByCategory, setItemsByCategory] = useState<Category[]>([]);
  const [itemsPreviewOnSubmitModal, setItemsPreviewOnSubmitModal] = useState<ItemPreview[]>([]);
  const [itemsRequest, setItemsRequest] = useState<ItemRequest[]>([]);
  const { hasPermissionTo, isAdmin } = useContext(UserContext);
  const hasPermissionToWrite = hasPermissionTo(['DailyWasteCheckSheetWrite']) || isAdmin();
  const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
  const [lastFocusedItemId, setLastFocusedItemId] = useState<number | null>(null);
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);
  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.lg}px)`);

  const { logError } = useLogError();

  const navigate = useNavigate();

  const INACTIVITY_TIMEOUT = 60000; // 1 min on milliseconds

  const { createWaste, submitWaste } = useNswagClient();
  const blocker = useBlocker(() => modifiedItems.size > 0);

  const { t } = useTranslation('wasteChecksheet');

  const hasRecentActivity = true;
  const isDailyWaste = true;
  const sort = 'ItemNumber';

  const isEditMode = !!(id && id !== '0');

  useEffect(() => {
    loadData();
  }, [selectedStore]);

  useEffect(() => {
    if (modifiedItems.size > 0 && isEditMode) {
      startInactivityTimer();
    }

    const beforeUnload = (event: { preventDefault: () => void; returnValue: string; }) => {
      if (modifiedItems.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [modifiedItems]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/waste-logs');
      if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, blocker]);

  useEffect(() => {
    const categorizedItems = groupCategoryItems(itemsData);
    setItemsByCategory(categorizedItems);
  }, [itemsData]);

  const loadData = () => {
    const storeNumber = selectedStore?.storeNumber;
    setIsLoading(true);

    // Call both API functions concurrently
    Promise.all([
      getItems(undefined, storeNumber, hasRecentActivity, undefined, undefined, undefined, undefined, undefined, undefined, isDailyWaste, sort),
      isEditMode && id ? getWasteById(parseInt(id, 10)) : Promise.resolve(null),
    ])
      .then(([itemsResult, wasteResult]) => {

        // Handle items data
        if (itemsResult?.data) {
          setItemsData(itemsResult.data);
        }

        // Handle waste data
        if (wasteResult?.data && itemsResult?.data) {
          mapWasteDataToItems(wasteResult.data, itemsResult.data);
        } else {
          console.log('ID is undefined');
        }

      })
      .catch((error) => {
        setIsLoading(false);
        logError(error);
      })
      .finally(() => setIsLoading(false));
  };

  const mapWasteDataToItems = (wasteData: WasteCheckSheetResponse, itemsDataToBeMapped: GetItemsResponse) => {
    const wasteItems = wasteData?.wasteHeaders?.items ?? [];
    if (wasteData.wasteHeaders?.submittedBy) {
      setIsArchived(true);
      setSubmittedDate(wasteData.wasteHeaders?.submittedDate);
      setSubmittedBy(wasteData.wasteHeaders?.submittedBy);
    }
    // Create a map for faster lookup
    const wasteItemsMap = new Map(wasteItems.map((item) => [item.itemId, item]));

    const updatedItemsData = itemsDataToBeMapped?.items?.map((item) => {
      const wasteItem = wasteItemsMap.get(item.id);
      if (wasteItem) {
        return {
          ...item,
          pastDueQty: wasteItem.pastDueQty,
          sampledQty: wasteItem.sampledQty,
          damagedQty: wasteItem.damagedQty,
          storeUseQty: wasteItem.storeUseQty,
        };
      }
      return item;
    });
    // Update the state with the updated itemsData
    setItemsData((prevState) => ({
      ...prevState,
      items: updatedItemsData,
    }));
  };

  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (isSaveSubmitEnabled()) {
      handleSaveConfirmModal();
    }
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const startInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    const timer = setTimeout(() => {
      if (modifiedItems.size > 0) {
        modifiedItems.forEach(itemId => {
          autosaveItem(itemId);
        });
        setIsDirty(false);
      }
    }, INACTIVITY_TIMEOUT);
    setInactivityTimer(timer);
  };

  const handleItemFocus = (itemId: number) => {
    if (lastFocusedItemId !== null && lastFocusedItemId !== itemId && modifiedItems.has(lastFocusedItemId)) {
      autosaveItem(lastFocusedItemId);
      setIsDirty(false);
    }
    setLastFocusedItemId(itemId);
  };

  //takes each item and changes the status from standard, idle, saving, saved. when the itemId matches the item
  //when the itemId is undefined it does all items, fromStatus is undefined then for all current status
  const updateItemStatus = (items: ItemWasteChecksheetData[], itemId: number | undefined, fromStatus: SaveStatus | undefined, toStatus: SaveStatus): ItemWasteChecksheetData[] => {
    const updateStatus = (currentStatus: SaveStatus | undefined): SaveStatus =>
      fromStatus === undefined || currentStatus === fromStatus ? toStatus : currentStatus ?? 'standard';

    return items.map(item =>
      itemId === undefined || item.id === itemId
        ? {
          ...item,
          pastDueQtySaveStatus: updateStatus(item.pastDueQtySaveStatus),
          sampledQtySaveStatus: updateStatus(item.sampledQtySaveStatus),
          damagedQtySaveStatus: updateStatus(item.damagedQtySaveStatus),
          storeUseQtySaveStatus: updateStatus(item.storeUseQtySaveStatus),
        }
        : item,
    );
  };

  const updateCategoryItems = (itemId: number | undefined, fromStatus: SaveStatus | undefined, toStatus: SaveStatus) => {
    if (parsedId != 0 || parsedId != undefined) {
      setItemsByCategory(prev => prev.map(category => ({
        ...category,
        items: updateItemStatus(category.items, itemId, fromStatus, toStatus),
      })));
    }
  };

  const autosaveItem = async (itemId: number) => {
    if (!itemId) return;

    updateCategoryItems(itemId, 'idle', 'saving');

    const requestData: WasteCheckSheetRequest = {
      id: parsedId,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: flattenAndFilterItems(),
    };

    try {
      await createWaste(requestData);

      setModifiedItems(prev => {
        const newModifiedItems = new Set(prev);
        newModifiedItems.delete(itemId);
        return newModifiedItems;
      });

      setTimeout(() => updateCategoryItems(itemId, 'saving', 'saved'), 2000);
      setTimeout(() => updateCategoryItems(itemId, 'saved', 'standard'), 5000);

    } catch (error) {
      setShowErrorSavedAlert(true);
      logError(error);
      updateCategoryItems(itemId, 'saving', 'error');
    }
  };

  const groupCategoryItems = (itemsData: ExtendedGetItemsResponse): Category[] => {
    if (!itemsData?.items?.length) {
      return [];
    }

    const categories: { [key: string]: Category } = {};

    for (const item of itemsData.items) {
      const category = item.primaryCategory?.replace(/\s+/g, ' ').trim().toUpperCase();
      if (category) {
        categories[category] = categories[category] ?? { name: category, items: [] };
        categories[category].items.push(item);
      }
    }

    const sortedCategories = Object.values(categories).sort((a, b) => a.name.localeCompare(b.name));
    return sortedCategories;
  };

  const resetItemQuantities = (category: Category) => {
    return category.items.map(item => ({
      ...item,
      pastDueQty: undefined,
      sampledQty: undefined,
      damagedQty: undefined,
      storeUseQty: undefined,
    }));
  };

  const resetCategoryItems = (prevItemsByCategory: Category[]) => {
    return prevItemsByCategory.map(category => ({
      ...category,
      items: resetItemQuantities(category),
    }));
  };

  const handleReset = () => {
    setItemsByCategory(prevItemsByCategory => resetCategoryItems(prevItemsByCategory));
    updateCategoryItems(undefined, undefined, 'standard');
  };

  const flattenAndFilterItems = (): ItemRequest[] => {
    const flattenItems = itemsByCategory.flatMap(category => category.items);

    const filteredItems: ItemRequest[] = flattenItems.filter(
      item =>
        (item.pastDueQty ?? 0) > 0 ||
        (item.sampledQty ?? 0) > 0 ||
        (item.damagedQty ?? 0) > 0 ||
        (item.storeUseQty ?? 0) > 0,
    ).map((item) => ({
      id: item.id ?? 0,
      itemNumber: item.itemNumber ?? '',
      description: item.description ?? '',
      uom: item.uom ?? '',
      pastDueQty: item.pastDueQty ?? 0,
      sampledQty: item.sampledQty ?? 0,
      damagedQty: item.damagedQty ?? 0,
      storeUseQty: item.storeUseQty ?? 0,
    }));

    setItemsRequest(filteredItems);
    return filteredItems;
  };

  const createPreviewItems = (itemsRequest: ItemRequest[]): ItemPreview[] => {
    const previewItems: ItemPreview[] = [];

    for (const item of itemsRequest) {
      for (const type of ['pastDueQty', 'sampledQty', 'damagedQty', 'storeUseQty'] as const) {
        const qty = item[type] ?? 0;
        if (qty > 0) {
          const reason = t(type);
          previewItems.push({
            itemNumber: item.itemNumber,
            description: item.description ? item.description : '',
            qty: qty,
            type: reason,
          });
        }
      }
    }

    return previewItems;
  };

  const isSaveSubmitEnabled = () => {
    const someNonZeroValue = itemsByCategory.flatMap(category => category.items).some(
      item =>
        (item.pastDueQty ?? 0) > 0 ||
        (item.sampledQty ?? 0) > 0 ||
        (item.damagedQty ?? 0) > 0 ||
        (item.storeUseQty ?? 0) > 0);

    if (itemsByCategory.length === 0 || isSaving || !isDirty || !someNonZeroValue) return false;

    return true;
  };

  const handleSubmitButton = () => {
    const filteredItems = flattenAndFilterItems();
    const previewItems = createPreviewItems(filteredItems);
    setItemsPreviewOnSubmitModal(previewItems);
    setOpenSubmitModal(true);
  };

  const handleConfirmSubmitModal = () => {
    setIsSubmitting(true);
    setIsDirty(false);

    const requestData: WasteCheckSheetRequest = {
      id: parsedId,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: itemsRequest,
    };
    submitWaste(requestData)
      .then(() => {
        setOpenSubmitModal(false);
        loadData();
        setIsSubmitting(false);
        setShowSubmitAlert(true);
      })
      .catch((error) => {
        setShowErrorSubmitAlert(true);
        setIsSubmitting(false);
        logError(error);
      });
  };

  const handleSubmitCancelModal = () => {
    setIsSubmitting(false);
    setOpenSubmitModal(false);
  };

  const handleSaveConfirmModal = () => {
    setIsSaving(true);
    setIsDirty(false);
    const filterItems = flattenAndFilterItems();

    const requestData: WasteCheckSheetRequest = {
      id: parsedId,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: filterItems,
    };

    createWaste(requestData)
      .then(() => {
        setModifiedItems(new Set());
        setIsSaving(false);
        setShowSavedAlert(true);

        if (!isEditMode) {
          setTimeout(() => {
            handleAfterSave();
          }, 1000);
        }

        updateCategoryItems(undefined, undefined, 'standard');
      })
      .catch(error => {
        setIsSaving(false);
        setShowErrorSavedAlert(true);
        logError(error);
      });
  };

  const handleQuantityChange = (categoryIndex: number, itemIndex: number, field: keyof ItemWasteChecksheetData, value: string) => {
    const updatedItemsByCategory = [...itemsByCategory];
    let parsedValue = value === '' ? null : (Math.round(Number(value) * 1000) / 1000);
    if (parsedValue && parsedValue < 0 ) parsedValue = 0;

    const itemToUpdate = updatedItemsByCategory[categoryIndex].items[itemIndex];
    const updatedItemId = itemToUpdate?.id;
    if (updatedItemId && !modifiedItems.has(updatedItemId)) {
      setModifiedItems(prev => new Set(prev.add(updatedItemId)));
      setIsDirty(true);
    }
    switch (field) {
      case 'pastDueQty':
        itemToUpdate.pastDueQty = parsedValue ?? undefined;
        if (!['saving', 'saved'].includes(itemToUpdate.pastDueQtySaveStatus ?? '')) {
          itemToUpdate.pastDueQtySaveStatus = 'idle';
        }
        break;
      case 'sampledQty':
        itemToUpdate.sampledQty = parsedValue ?? undefined;
        if (!['saving', 'saved'].includes(itemToUpdate.sampledQtySaveStatus ?? '')) {
          itemToUpdate.sampledQtySaveStatus = 'idle';
        }
        break;
      case 'damagedQty':
        itemToUpdate.damagedQty = parsedValue ?? undefined;
        if (!['saving', 'saved'].includes(itemToUpdate.damagedQtySaveStatus ?? '')) {
          itemToUpdate.damagedQtySaveStatus = 'idle';
        }
        break;
      case 'storeUseQty':
        itemToUpdate.storeUseQty = parsedValue ?? undefined;
        if (!['saving', 'saved'].includes(itemToUpdate.storeUseQtySaveStatus ?? '')) {
          itemToUpdate.storeUseQtySaveStatus = 'idle';
        }
        break;
      default:
        break;
    }
    setItemsByCategory(updatedItemsByCategory);
  };

  const handleAfterSave = () => {
    navigate('/store/waste-logs');
  };

  const printTableContainer = () => {
    setIsPrinting(true);
    //time out to switch view to desktop, async so small wait to make sure it's changed
    setTimeout(function () {
      const printableGridItems = document.querySelectorAll('.waste-checksheet-categories');

      if (printableGridItems.length > 0) {
        // Hide the fieldsets with class MuiOutlinedInput-notchedOutline
        printableGridItems.forEach((printableGridItem) => {
          let fieldsets = printableGridItem.querySelectorAll('.MuiOutlinedInput-notchedOutline');
          fieldsets.forEach((fieldset) => {
            (fieldset as HTMLElement).style.display = 'none';
          });
          fieldsets = printableGridItem.querySelectorAll('.hide-on-print');
          fieldsets.forEach((fieldset) => {
            (fieldset as HTMLElement).style.display = 'none';
          });
        });

        const WinPrint = window.open('', '', 'left=0,top=0,width=1650,height=900,toolbar=0,scrollbars=0,status=0');

        if (WinPrint) {

          // Open the new window and begin writing the content
          WinPrint.document.write('<html><head><title>Print</title>');
          WinPrint.document.write('<style>');
          WinPrint.document.write(`
        table { table-layout: fixed; }
          .waste-checksheet-categories {margin-bottom: 20px}
          .waste-checksheet-categories .title {font-size: 1.3em; color: #636363; margin-bottom: 5px;}
        th{text-align: left;}
        .description { width: 300px;}
        td {border-top: 1px solid #ddd; overflow: hidden; padding: 15px 0px; font-size: 10px; white-space: normal;}
        .uom {padding-left: 10px;}
        tr:first-child > td {border-top: 2px solid #ddd;}
        tr {border-top: 1px solid #ddd;}
        .normal-field {width:150px;}
        .normal-field input {width: 50px;height: 30px; border-radius: 5px !important;border: 2px solid darkgray; display: flex; align-items: center; text-align: center; }
        @media print {.stock-list {page-break-after: always;&:last-of-type {page-break-after: avoid;}}}
        `);
          WinPrint.document.write('</style></head><body>');

          // Loop through each Grid item and append its content to the print window
          printableGridItems.forEach((grid) => {
            WinPrint.document.write('<div class="waste-checksheet-categories">');
            WinPrint.document.write(grid.innerHTML);
            WinPrint.document.write('</div>');
          });

          WinPrint.document.write('</body></html>');
          WinPrint.document.close();
          WinPrint.focus();

          // Delay the print function by a short timeout to ensure content is fully loaded
          setTimeout(() => {
            // Initiate the print action
            WinPrint.print();
            WinPrint.close();
            // Restore visibility of input fields after printing
            printableGridItems.forEach((printableGridItem) => {
              const fieldsets = printableGridItem.querySelectorAll('.MuiOutlinedInput-notchedOutline');
              fieldsets.forEach((fieldset) => {
                (fieldset as HTMLElement).style.display = '';
              });
            });
          }, 500);
        }
      }

      setIsPrinting(false);
    }, 200);

  };

  return {
    t,
    isLoading,
    isDirty,
    showSubmitAlert,
    itemsData,
    itemsByCategory,
    isArchived,
    submittedBy,
    submittedDate,
    loadData,
    updateCategoryItems,
    handleLeavePageModalCancel,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    handleItemFocus,
    handleSaveConfirmModal,
    handleSubmitButton,
    handleSubmitCancelModal,
    handleReset,
    isSaving,
    isSubmitting,
    showSavedAlert,
    showErrorSavedAlert,
    showErrorSubmitAlert,
    openSubmitModal,
    setShowSavedAlert,
    setShowErrorSavedAlert,
    setShowErrorSubmitAlert,
    printTableContainer,
    hasPermissionToWrite,
    isMobile,
    isSaveSubmitEnabled,
    isEditMode,
    handleQuantityChange,
    isPrinting,
    handleConfirmSubmitModal,
    itemsPreviewOnSubmitModal,
    modifiedItems,
    blocker,
    setShowSubmitAlert,
  };
};
